import React, { ChangeEvent, FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { FormHelperText, IconButton, SvgIcon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import i18n from '../../../i18n';
import { WrappedFieldProps } from 'redux-form';
import clsx from 'clsx';

import { Image as ImageComponent, ImageSize } from 'components/Image/Image';

import { CroppedArea } from 'types';

import { Text, TextTypography } from 'components/Text/Text';
import { Button, ButtonColor } from 'components/Button/Button';
import { CoverImageControlPopup } from './CoverImageControlPopup';
import { CropperMode } from './types';
import { ReactComponent as DeleteIcon } from 'assets/icons/additional/red-trash.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/additional/image-edit-2.svg';

export interface StoreImageControlProps {
    mode: CropperMode;
    label: string;
    value: string;
    onChange: (value: File) => void;
    cropSize: {
        width: number;
        height: number;
    };
    cropAspect: number;
    // Optional:
    className?: string;
    emptyClassName?: string;
    imgClassName?: string;
    error?: string;
    onChangeCrop: (value: CroppedArea) => void;
}

const useStyles = makeStyles({
    image: {
        width: '100%',
        margin: 'auto',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        willChange: 'transform',
    },
    imageWrapper: {
        position: 'absolute',
        zIndex: 'auto',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
    },
    blob: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 20,
    },
    imageDelete: {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        borderRadius: 16,
        bottom: 24,
        cursor: 'pointer',
        zIndex: 2,
        height: 32,
        padding: 8,
        position: 'absolute',
        right: 150,
        transition: 'background-color 0.2s ease',
        width: 32,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
        },
    },
    changeButton: {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        bottom: 24,
        borderRadius: 100,
        zIndex: 2,
        position: 'absolute',
        right: 24,
        transition: 'background-color 0.2s ease',
    },
    //empy image
    emptyImageContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        justifyContent: 'center',
        alignItems: 'center',
        background: 'rgba(15, 24, 37, 0.025)',
        borderRadius: 10,
        border: '2px dashed rgba(36, 49, 67, 0.2)',
    },
    storeActionButton: {
        marginTop: 16,
    },
    centeredText: {
        textAlign: 'center',
    },
    editButton: {
        bottom: 8,
        padding: 0,
        position: 'absolute',
        right: 8,
        zIndex: 1,
    },
    deleteButton: {
        bottom: 8,
        padding: 0,
        position: 'absolute',
        left: 8,
        zIndex: 1,
    },
});

export const StoreImageControl: FC<StoreImageControlProps> = ({
    mode,
    error,
    value,
    cropSize,
    cropAspect,
    imgClassName,
    onChange,
    onChangeCrop,
}) => {
    const fileInput = useRef<HTMLInputElement>();
    const { t } = i18n.useTranslation();

    const [displayUrl, setDisplayUrl] = useState(value);
    useEffect(() => {
        if (!!value && typeof value === 'string') {
            setDisplayUrl(value);
        }
    }, [value]);

    const [previewUrl, setPreviewUrl] = useState(null);

    const [isOpen, setIsOpen] = useState(false);

    const onFileChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files[0];
            if (file) {
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
                setDisplayUrl(url);
            } else {
                setPreviewUrl(null);
                setDisplayUrl(null);
            }
        },
        [setPreviewUrl, setDisplayUrl],
    );

    const onOpen = useCallback(() => setIsOpen(true), [setIsOpen]);
    const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);

    const onUpdate = useCallback(() => {
        if (onChange) {
            const file = fileInput.current.files[0];
            onChange(file);
            if (file) {
                const url = URL.createObjectURL(file);
                setDisplayUrl(url);
            } else {
                setDisplayUrl(null);
            }
        }
    }, [onChange, fileInput, setDisplayUrl]);

    const onDelete = useCallback(() => {
        setDisplayUrl(null);
        setPreviewUrl(null);
        fileInput.current.value = '';
        if (onChange) {
            onChange(null);
        }
    }, [fileInput]);

    const onOpenFileDialog = useCallback(() => fileInput.current.click(), [fileInput]);

    const imageUrl = displayUrl || previewUrl;

    const [previewElement, setPreviewElement] = useState<HTMLDivElement>();

    const classes = useStyles();

    return (
        <Fragment>
            <div style={{ maxHeight: cropSize.height }}>
                <input
                    accept=".jpg,.jpeg,.png"
                    multiple={false}
                    type="file"
                    ref={r => (fileInput.current = r)}
                    hidden={true}
                    onChange={onFileChange}
                />
                <div
                    style={{
                        height: '100%',
                        position: 'relative',
                    }}
                >
                    {imageUrl ? (
                        <Fragment>
                            <div className={classes.blob}>
                                <div
                                    style={{
                                        position: 'relative',
                                        width: cropSize.width,
                                        height: cropSize.height,
                                    }}
                                >
                                    <div className={clsx(classes.imageWrapper, imgClassName)} onClick={onOpen}>
                                        {imageUrl.startsWith('blob') ? (
                                            <div className={classes.image} ref={setPreviewElement} onClick={onOpen} />
                                        ) : (
                                            <ImageComponent
                                                style={{ zIndex: 0, position: 'absolute' }}
                                                imageId={imageUrl}
                                                description=""
                                                size={ImageSize.RECTANGULAR}
                                            />
                                        )}
                                    </div>
                                    <IconButton className={classes.deleteButton} onClick={onDelete}>
                                        <SvgIcon component={DeleteIcon} viewBox="0 0 32 32" />
                                    </IconButton>
                                    <IconButton className={classes.editButton} onClick={onOpen}>
                                        <SvgIcon component={EditIcon} viewBox="0 0 24 24" />
                                    </IconButton>
                                </div>
                            </div>
                        </Fragment>
                    ) : (
                        // <CoverImageControlEmpty className={emptyClassName} label={label} />
                        <div className={classes.emptyImageContainer}>
                            <Text typography={TextTypography.CAPTION} className={classes.centeredText}>
                                {t('common:imageControl.coverText')}
                            </Text>
                            <Button className={classes.storeActionButton} color={ButtonColor.GRAY} onClick={onOpen}>
                                <Text typography={TextTypography.PARA_ACCENT}>{t('common:imageControl.add')}</Text>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            {error && <FormHelperText error={true}>{error}</FormHelperText>}
            {isOpen && (
                <CoverImageControlPopup
                    mode={mode}
                    displayedValue={previewUrl || displayUrl}
                    onChange={onUpdate}
                    cropSize={cropSize}
                    cropAspect={cropAspect}
                    onClose={onClose}
                    onDelete={onDelete}
                    onOpenFileDialog={onOpenFileDialog}
                    onChangeCrop={onChangeCrop}
                    previewElement={previewElement}
                    hideRemoveButton
                />
            )}
        </Fragment>
    );
};

export interface CoverImageControlFieldOwnProps {
    mode: CropperMode;
    label: string;
    cropSize: {
        width: number;
        height: number;
    };
    cropAspect: number;
    // Optional:
    classNames?: {
        className?: string;
        emptyClassName?: string;
        imgClassName?: string;
    };
    onChangeCrop: (croppedArea: CroppedArea) => void;
}

export type StoreImageControlFieldProps = CoverImageControlFieldOwnProps & WrappedFieldProps;

export const StoreImageControlField = (props: StoreImageControlFieldProps) => {
    const { t } = i18n.useTranslation();
    const {
        mode,
        input,
        label,
        cropSize,
        cropAspect,
        classNames,
        onChangeCrop,
        meta: { touched, invalid, error },
    } = props;
    return (
        <StoreImageControl
            mode={mode}
            onChangeCrop={onChangeCrop}
            label={label}
            cropSize={cropSize}
            cropAspect={cropAspect}
            error={touched && invalid ? t(error) : undefined}
            className={classNames?.className}
            emptyClassName={classNames?.emptyClassName}
            imgClassName={classNames?.imgClassName}
            {...input}
        />
    );
};
